import { BarChart3, Zap, Brain, Eye, Shield } from "lucide-react";
import { INDUSTRY_OPTIONS } from "../../lib/roiCalculator";

export const MIN_COUNT_TO_DISPLAY = 5; // avoid displaying a sparse-looking count

// Only the three core verticals get a tailored follow-up question; everyone else (including
// Other/custom industries) proceeds straight into the universal assessment questions.
const INDUSTRY_SPECIFIC_QUESTIONS = {
  "Financial Services": { id: "industry_specific", text: "How audit-ready is your compliance documentation (SOC 2, GLBA, etc.)?", options: [
    { text: "Fully documented and audit-ready", score: 0 },
    { text: "Some documentation, but gaps exist", score: 5 },
    { text: "Not audit-ready / no formal process", score: 10 },
  ]},
  "Construction": { id: "industry_specific", text: "How connected are your job sites to your central systems?", options: [
    { text: "Fully connected - real-time data from every site", score: 0 },
    { text: "Partially connected - some sites lack connectivity", score: 5 },
    { text: "Disconnected - manual updates from the field", score: 10 },
  ]},
  "Manufacturing": { id: "industry_specific", text: "How well are your OT/production systems (PLCs, SCADA, machinery) protected from cyber risk?", options: [
    { text: "Segmented and actively monitored", score: 0 },
    { text: "Some protections, not fully segmented", score: 5 },
    { text: "Little to no OT security", score: 10 },
  ]},
};

export function getSteps(answers) {
  const industrySpecific = INDUSTRY_SPECIFIC_QUESTIONS[answers.industry?.text];
  return [
    {
      category: "Business Context",
      icon: BarChart3,
      questions: [
        { id: "industry", text: "What industry is your business in?", options: INDUSTRY_OPTIONS },
        { id: "team_size", text: "How many people are on your team?", options: ["1-10", "11-50", "51-200", "200+"] },
        ...(industrySpecific ? [industrySpecific] : []),
      ],
    },
    {
    category: "Operational Efficiency",
    icon: Zap,
    questions: [
      { id: "manual_work_level", text: "How much of your daily work relies on manual processes?", options: [
        { text: "Very little - most is automated", score: 0 },
        { text: "Some tasks are still manual", score: 5 },
        { text: "Most workflows are manual", score: 10 },
      ]},
      { id: "system_visibility", text: "Can you see your entire operation in one place?", options: [
        { text: "Yes - unified dashboards", score: 0 },
        { text: "Partially - some tools connected", score: 5 },
        { text: "No - siloed systems everywhere", score: 10 },
      ]},
      { id: "weekly_manual_hours", text: "On average, how many hours per week does each team member spend on manual, repetitive tasks (data entry, scheduling, reporting, etc.)?", options: [
        "Less than 5 hours", "5-10 hours", "10-20 hours", "20+ hours",
      ]},
    ],
  },
  {
    category: "Automation Maturity",
    icon: Zap,
    questions: [
      { id: "automation_usage", text: "How would you describe your current use of automation?", options: [
        { text: "Automated workflows across departments", score: 0 },
        { text: "Some automation in select areas", score: 5 },
        { text: "Little to no automation", score: 10 },
      ]},
      { id: "repetitive_tasks", text: "How often does your team repeat the same tasks manually?", options: [
        { text: "Rarely - processes are streamlined", score: 0 },
        { text: "Sometimes - some redundancy", score: 5 },
        { text: "Daily - significant repetition", score: 10 },
      ]},
    ],
  },
  {
    category: "AI Readiness",
    icon: Brain,
    questions: [
      { id: "ai_usage_flag", text: "Is your organization currently using any AI tools?", options: [
        { text: "Yes - governed and integrated", score: 0 },
        { text: "Some employees use AI informally", score: 5 },
        { text: "No AI usage or awareness", score: 10 },
      ]},
      { id: "ai_governance", text: "Do you have policies governing AI use in your organization?", options: [
        { text: "Yes - formal AI governance", score: 0 },
        { text: "Informal guidelines", score: 5 },
        { text: "No policies at all", score: 10 },
      ]},
    ],
  },
  {
    category: "Access & Data Control",
    icon: Eye,
    questions: [
      { id: "access_visibility", text: "Do you know who has access to what data across your organization?", options: [
        { text: "Full visibility with role-based controls", score: 0 },
        { text: "Partial visibility", score: 5 },
        { text: "Limited or no visibility", score: 10 },
      ]},
      { id: "data_protection", text: "How confident are you that sensitive data is properly protected?", options: [
        { text: "Very confident - controls in place", score: 0 },
        { text: "Somewhat - some gaps exist", score: 5 },
        { text: "Not confident", score: 10 },
      ]},
    ],
  },
  {
    category: "Business Continuity",
    icon: Shield,
    questions: [
      { id: "continuity_readiness", text: "If your systems went down today, how quickly could you recover?", options: [
        { text: "Within hours - tested plan in place", score: 0 },
        { text: "Within days - some planning done", score: 5 },
        { text: "Unsure - no tested plan", score: 10 },
      ]},
      { id: "monitoring", text: "Are your systems monitored for issues before they become problems?", options: [
        { text: "Yes - 24/7 proactive monitoring", score: 0 },
        { text: "During business hours only", score: 5 },
        { text: "No - we react when things break", score: 10 },
      ]},
    ],
  },
  ];
}

export function getScoreLabel(pct) {
  if (pct >= 80) return { label: "Strong", color: "#10b981" };
  if (pct >= 50) return { label: "Developing", color: "#eab308" };
  return { label: "Needs Attention", color: "#ef4444" };
}
